import { Component, OnInit, Input, OnDestroy, SimpleChanges, OnChanges, EventEmitter, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { Actions } from 'src/app/models/actions';
import { VehicleSchedule } from 'src/app/models/vehicleSchedule';
import { FleetSchedule } from 'src/app/models/fleetSchedule';
import { DynamicTableInterface } from 'src/app/models/interface/dynamicTable';
import { TenantState } from 'src/app/store/state/tenant.state';
import { Select } from '@ngxs/store';
import { Observable, Subject, forkJoin } from 'rxjs';
import { ScheduleService } from '../schedule/schedule.service';
import { VehicleService } from '../vehicle/service/vehicle.service';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';
import { TranslateService } from '@ngx-translate/core';
import { DynamicDialogService } from 'src/app/dynamic-dialog/dynamic-dialog.service';
import { ScheduleCriteriaPipe } from 'src/app/shared/pipes/scheduleCriteria';
import { isEmpty } from 'lodash';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { GenericValidators } from 'src/app/shared/generic-validators';
import { SpaRoutingService } from 'src/app/shared/services/spa.routing.service';
import { MaintainVehicleSchedule } from 'src/app/models/constant/right';

@Component({
    selector: 'app-vehicle-schedule',
    templateUrl: './vehicle-schedule.component.html',
    styleUrls: ['./vehicle-schedule.component.css'],
    standalone: false
})

export class VehicleScheduleComponent implements OnInit, OnChanges, OnDestroy, DynamicTableInterface {
  @Select(TenantState.getCurrentTenantExternalId) getCurrentTenantExternalId$: Observable<string>;
  vehicleSchedules: VehicleSchedule[];
  fleetSchedules: FleetSchedule[];
  @Input() vin: string;
  @Input() tenantId: string;
  @Input() refreshSchedules: number;
  @Output() public functionCallback = new EventEmitter();

  readonly waitInterval = 500; // 1/2 second

  Actions: typeof Actions = Actions;
  loading = true;
  dataSource = new MatTableDataSource<any>();
  baseSort = 'recommendedServiceDate:1, "schedule.category":1, "schedule.action":1, "schedule.name":1';
  private currentSort = `{${this.baseSort}}`;
  private currentFilter = '{}';
  private currentOffset = 0;
  private currentLimit = 10;
  private destroy$ = new Subject();
  private activeFilter: string = '{isActive: true}';
  private showInactive: boolean = false;

  selection = new SelectionModel<any>(true, []);

  searchString = `{$or: [
    {"schedule.name": /|replaceText|/i},
    {"schedule.category": /|replaceText|/i}
  ]}`;
  includeSelect = true;
  gridDisplayColumns = [
    {
      display: '@active',
      property: 'isActive',
      type: 'boolean'
    },
    {
      display: '@name',
      property: 'schedule.name',
      type: 'nestedText'
    },
    {
      display: '@action',
      property: 'schedule.action',
      type: 'nestedEnum',
      enum: Actions
    },
    {
      display: '@category',
      property: 'schedule.category',
      type: 'nestedText'
    },
    {
      display: '@interval',
      property: 'currentCriteriaText',
      type: 'text'
    },
    {
      display: '@fleetSchedule',
      property: 'fleetScheduleName',
      type: 'text',
      hidden: true
    },
    {
      display: '@lastServiceDate',
      property: 'lastServiceDate',
      type: 'date',
      hidden: true
    },
    {
      display: '@recommendedServiceDate',
      property: 'recommendedServiceDate',
      type: 'date'
    }
  ];
  tableFunctions = [
    {
      display: '@scheduleMaintenance',
      action: 'scheduleMaintenance',
      icon: 'fa fa-wrench',
      validators: (rows) => this.validators.selectedLengthValidator(rows, ">=", 1),
      requireRight: () => this.validators.hasRight(MaintainVehicleSchedule)
    },
    {
      display: '@recalculate',
      action: 'recalculate',
      icon: 'fa fa-calculator',
      requireRight: () => this.validators.hasRight(MaintainVehicleSchedule)
    },
    {
      display: '@toggleInactiveSchedules',
      action: 'toggleInactive',
      icon: 'fa fa-eye'
    },
    {
      display: '@showAllColumns',
      action: 'show',
      icon: 'fa fa-eye'
    }
  ];
  length: number;

  constructor(
    private scheduleCriteriaPipe: ScheduleCriteriaPipe,
    private dialogService: DynamicDialogService,
    private scheduleService: ScheduleService,
    private vehicleService: VehicleService,
    private toastGlobalService: ToastGlobalService,
    private translate: TranslateService,
    private router: Router,
    private validators: GenericValidators,
    private spaRoutingService: SpaRoutingService
  ) { }

  ngOnInit(): void {
    this.getCurrentTenantExternalId$
    .pipe(takeUntil(this.destroy$))
    .subscribe(externalId => {
      if (externalId) {
        this.tenantId = externalId;
      }
    });
    this.getSchedules();
    
  }
  getSchedules(){
    let filter: string;
    if(!this.showInactive){
      filter = `{$and: [${this.currentFilter}, ${this.activeFilter}]}`;
    }
    else {
      filter = this.currentFilter;
    }
    this.getData(
      this.currentOffset,
      this.currentLimit,
      filter,
      this.currentSort
    );
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.refreshSchedules && !changes.refreshSchedules.firstChange) {
      this.getSchedules();      
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  toggleShowColumns(showAllColumn: boolean): void {
    this.gridDisplayColumns.forEach(col => {
      if (showAllColumn) {
        if (col.hidden === true) {
          col.hidden = false;
        }
      }
      else {
        if (col.hidden === false) {
          col.hidden = true;
        }
      }
    });
  }

  processFunction(event: any): void {
    switch (event.action) {
      case 'toggleInactive':
        this.showInactive = !this.showInactive;
        this.getSchedules();
        break;
      case 'navigate':
        this.selectRowEvent(event);
        break;
      case 'page':
        this.pageChangedEvent(event);
        break;
      case 'filter':
        this.filterChanged(event.filter);
        break;
      case 'sort':
        this.sortDataEvent(event.sort);
        break;
      case 'refresh':
        this.refreshDataEvent(event);
        break;
      case 'show':
        this.toggleShowColumns(event.showAllColumns);
        break;
      case 'recalculate':
        this.recalculate();
        break;
      case 'scheduleMaintenance':
        const allSelected = event.allSelected;
        const rowsSelected = event.rowsSelected;
        this.scheduleMaintenance(allSelected, rowsSelected);
        break;
    }
  }

  pageChangedEvent(event): void {
    const pageIndex = event.pageIndex;
    const pageSize = event.pageSize;
    this.currentLimit = pageSize;
    const previousSize = pageSize * pageIndex;
    this.currentOffset = previousSize;
    this.getSchedules();
  }

  sortDataEvent(sort: string): void {
    this.currentSort = sort;
    this.currentOffset = 0;
    this.getSchedules();
  }

  getData(offset: number, limit: number, filter: string, sort: string): void {
    this.loading = true;
    const promises = [];

    const observables = [];
    if (!this.fleetSchedules) {
      observables.push(this.scheduleService.getAllFleetSchedules(JSON.stringify({}), JSON.stringify({}), 0, 1000)
        .pipe(tap(fs => this.fleetSchedules = fs.documents)));
    }
    observables.push(this.vehicleService.getVehicleSchedule(this.vin, filter, sort, offset, limit)
      .pipe(tap(res => {
        this.vehicleSchedules = res.documents;
        this.dataSource = new MatTableDataSource(res.documents);
        this.length = res.count;
      })));
    
      forkJoin(observables).subscribe(() => {
        this.loading = false;
        this.dataSource.data.forEach(vs => {
          this.changeData(vs);
        });
      })
  }

  filterChanged(filterList: string): void {
    this.currentFilter = filterList;
    this.currentOffset = 0;
    this.getSchedules();
  }

  selectRowEvent(event: any): void {
    this.vehicleService.selectedSchedule = event.row;
    this.spaRoutingService.navigateToVehicleSchedule(this.vin, this.vehicleService.selectedSchedule.id);
  }

  refreshDataEvent(event: any): void {
    const pageIndex = event.pageIndex;
    const pageSize = event.pageSize;
    this.currentLimit = pageSize;
    const previousSize = pageSize * pageIndex;
    this.currentOffset = previousSize;
    this.currentFilter = event.filter;
    if (event.sortString !== '{}') {
      this.currentSort = event.sortString;
    }

    this.getSchedules();

    this.toggleShowColumns(event.showAllColumns);

  }

  recalculate(): void {
    this.loading = true;
    this.scheduleService.recalculateVin(this.vin).subscribe(res => {
      this.getSchedules();
      this.loading = false;
    });
  }

  scheduleMaintenance(allSelected: boolean, rowsSelected: any[]): void {
    if (!allSelected && rowsSelected.length < 1) {
      this.toastGlobalService.show('You must have at least one row selected.', { classname: 'error' });
    }
    else {
      if (allSelected) {
        rowsSelected = this.vehicleSchedules;
      }

      const payload = {
        complete: false
      };

      this.dialogService.openAddMaintenanceDialog(payload).afterClosed()
        .pipe(
          filter(res => res),
          switchMap(res => this.scheduleService.scheduleMaintenance(
            this.vin,
            rowsSelected,
            res.currentSelected,
            res.currentDate,
            res.hoursTaken)))
        .subscribe(ret => {
          const toast = this.translate.instant('Maintenance Scheduled');
          this.toastGlobalService.show(toast, { classname: 'success' });
          const newEvent = {
            action: 'refreshMaintenances'
          };
          this.functionCallback.emit(newEvent);

          setTimeout(() => {
            this.getSchedules();
          }, this.waitInterval);
        });
    }
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  public masterToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  public checkboxLabel(row?: VehicleSchedule): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id}`;
  }

  private changeData(vs: VehicleSchedule): VehicleSchedule {
    if (vs.currentCriteria.length > 0) {
      vs.currentCriteria[0].criteriaText = this.scheduleCriteriaPipe.transform(vs.currentCriteria[0]);
      vs.currentCriteriaText = vs.currentCriteria[0].criteriaText;
    }
    else {
      vs.currentCriteriaText = 'No Criteria';
    }
    if (vs.lastMaintenance) {
      if (vs.lastMaintenance.date.completeDate) {
        vs.lastServiceDate = new Date(vs.lastMaintenance.date.completeDate);
      }
      else {
        vs.lastServiceDate = new Date(vs.lastMaintenance.createDateTimeUtc);
      }
    }

    if (!vs.recommendedServiceDate) {
      if (vs.lastServiceDate) {
        vs.recommendedServiceDate = new Date();
        vs.recommendedServiceDate.setDate(vs.lastServiceDate.getDate() + 100);
      }
    }

    if (!isEmpty(vs.fleetScheduleId)) {
      vs.fleetScheduleName = this.fleetSchedules.find((item: FleetSchedule) => item.id === vs.fleetScheduleId)?.name;
    }

    return vs;
  }
}
